import React, { useState, useMemo } from 'react';
import { FaChevronDown, FaChevronUp, FaLock, FaUnlock, FaGift, FaPercent, FaStar, FaTrophy } from 'react-icons/fa';
import {
  CANDY_VERSIONS,
  VERSION_ORDER,
  CANDIES,
  CANDY_ORDER,
  LEVEL_CONFIG,
  PAYTABLE,
  formatChance,
  getLevelUnlocks,
} from '../../data/candyConfig';
import { getCandyBaseLogo, getCandyLogo } from '../../data/candyLogos';
import './BenefitsList.css';

/**
 * BenefitsList - Shows the paytable/rules and level unlock benefits
 * Displays real candy odds for each level with accurate data from candyConfig
 */
const BenefitsList = ({ currentLevel = 0 }) => {
  const [selectedLevel, setSelectedLevel] = useState(currentLevel);
  const [expandedCandies, setExpandedCandies] = useState({});
  const [viewMode, setViewMode] = useState('compact'); // 'compact' or 'detailed'

  // Get paytable data for selected level
  const paytableData = useMemo(() => {
    const levelData = PAYTABLE[selectedLevel];

    // Level not yet defined
    if (!levelData) {
      return { available: false, candies: [] };
    }

    const candies = CANDY_ORDER.map(candyId => {
      const candyConfig = CANDIES[candyId];
      const candyData = levelData[candyId];
      const candyLogo = getCandyBaseLogo(candyId);

      if (candyData === null) {
        return {
          id: candyId,
          name: candyConfig.name,
          icon: candyConfig.icon,
          logo: candyLogo,
          available: false,
          versions: [],
        };
      }

      const versions = VERSION_ORDER.map(version => ({
        name: version,
        chance: candyData[version],
        config: CANDY_VERSIONS[version],
        logo: getCandyLogo(candyId, version),
      }));

      return {
        id: candyId,
        name: candyConfig.name,
        icon: candyConfig.icon,
        logo: candyLogo,
        available: true,
        versions,
      };
    });

    return { available: true, candies };
  }, [selectedLevel]);

  // Toggle candy expansion
  const toggleCandy = (candyId) => {
    setExpandedCandies(prev => ({
      ...prev,
      [candyId]: !prev[candyId]
    }));
  };

  // Level unlock milestones
  const levelUnlocks = useMemo(() => getLevelUnlocks(), []);

  return (
    <div className="benefits-list-container">
      {/* Header */}
      <div className="benefits-header">
        <div className="header-icon">
          <FaGift />
        </div>
        <div className="header-text">
          <h3>Paytable & Odds</h3>
          <p>Higher levels = More candies & better chances!</p>
        </div>
      </div>

      {/* Level Selector */}
      <div className="level-selector">
        <label className="selector-label">Preview Level:</label>
        <div className="selector-buttons">
          {Array.from({ length: LEVEL_CONFIG.DEFINED_LEVELS + 1 }).map((_, lvl) => (
            <button
              key={lvl}
              className={`level-btn ${selectedLevel === lvl ? 'selected' : ''} ${lvl > currentLevel ? 'locked' : ''}`}
              onClick={() => setSelectedLevel(lvl)}
              title={lvl > currentLevel ? `Preview Level ${lvl} rewards` : `Level ${lvl}`}
            >
              {lvl}
            </button>
          ))}
        </div>
        {selectedLevel > currentLevel && (
          <div className="preview-notice">
            <FaUnlock /> Previewing Level {selectedLevel} rewards
          </div>
        )}
      </div>

      {/* View Mode Toggle */}
      <div className="view-mode-toggle">
        <button
          className={`mode-btn ${viewMode === 'compact' ? 'active' : ''}`}
          onClick={() => setViewMode('compact')}
        >
          Compact
        </button>
        <button
          className={`mode-btn ${viewMode === 'detailed' ? 'active' : ''}`}
          onClick={() => setViewMode('detailed')}
        >
          Detailed
        </button>
      </div>

      {/* Paytable Content */}
      {!paytableData.available ? (
        <div className="coming-soon-message">
          <FaTrophy className="trophy-icon" />
          <span>Level {selectedLevel} data coming soon!</span>
          <p>Check back later for updated odds.</p>
        </div>
      ) : (
        <div className="paytable-content">
          {/* DryPull Info - Fixed 50% at all levels */}
          <div className="candy-section drypull-section">
            <div className="candy-header drypull-header">
              <div className="candy-info">
                <span className="candy-icon">💨</span>
                <span className="candy-name">DryPull (No Candy)</span>
                <span className="drypull-chance">50%</span>
              </div>
            </div>
            <div className="drypull-note">
              <FaPercent className="note-icon" />
              <span>50% chance of empty pull at all levels</span>
            </div>
          </div>
          {paytableData.candies.map((candy) => {
            const isExpanded = expandedCandies[candy.id];

            return (
              <div
                key={candy.id}
                className={`candy-section ${candy.available ? 'available' : 'unavailable'} ${isExpanded ? 'expanded' : ''}`}
              >
                {/* Candy Header */}
                <div
                  className="candy-header"
                  onClick={() => candy.available && toggleCandy(candy.id)}
                >
                  <div className="candy-info">
                    <div className="candy-logo">
                      {candy.logo ? (
                        <img src={candy.logo} alt={`${candy.name} logo`} loading="lazy" />
                      ) : (
                        <span className="candy-icon">{candy.icon}</span>
                      )}
                    </div>
                    <span className="candy-name">{candy.name}</span>
                    {!candy.available && (
                      <span className="unavailable-badge">
                        <FaLock /> Not at this level
                      </span>
                    )}
                  </div>
                  {candy.available && (
                    <div className="expand-icon">
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  )}
                </div>

                {/* Version Grid (when expanded or in detailed mode) */}
                {candy.available && (isExpanded || viewMode === 'detailed') && (
                  <div className="versions-grid">
                    {candy.versions.map((version) => (
                      <div
                        key={version.name}
                        className="version-item"
                        style={{ borderColor: version.config.color }}
                        title={`${version.name} · ${formatChance(version.chance)}`}
                      >
                        <div className="version-logo" style={{ borderColor: version.config.color }}>
                          {version.logo ? (
                            <img src={version.logo} alt={`${candy.name} ${version.name} logo`} loading="lazy" />
                          ) : (
                            <span className="version-fallback">{candy.icon}</span>
                          )}
                        </div>
                        <div className="version-meta">
                          <div
                            className="version-badge"
                            style={{
                              background: version.config.gradient,
                              color: '#fff'
                            }}
                          >
                            {version.name}
                          </div>
                          <div className="version-chance">
                            <FaPercent className="percent-icon" />
                            <span>{formatChance(version.chance)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Compact Preview (when not expanded in compact mode) */}
                {candy.available && !isExpanded && viewMode === 'compact' && (
                  <div className="compact-preview">
                    <div className="compact-versions">
                      {candy.versions.map((version) => (
                        <div
                          key={version.name}
                          className="compact-version"
                          style={{ borderColor: version.config.color }}
                          title={`${version.name} · ${formatChance(version.chance)}`}
                        >
                          {version.logo ? (
                            <img src={version.logo} alt={`${candy.name} ${version.name}`} loading="lazy" />
                          ) : (
                            <span className="compact-fallback">{candy.icon}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    <span className="compact-hint">Tap for odds</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Version Tier Legend */}
      <div className="version-legend">
        <h4 className="legend-title">
          <FaStar />
          <span>Version Tiers</span>
        </h4>
        <div className="legend-grid">
          {VERSION_ORDER.map((version) => {
            const config = CANDY_VERSIONS[version];
            return (
              <div
                key={version}
                className="legend-item"
                style={{ borderColor: config.color }}
              >
                <span
                  className="legend-dot"
                  style={{ background: config.color }}
                />
                <span className="legend-name">{config.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Level Unlock Milestones */}
      <div className="benefits-summary">
        <h4 className="summary-title">
          <FaUnlock />
          <span>Level Unlocks</span>
        </h4>
        <div className="milestones-list">
          {levelUnlocks.map((item) => (
            <div
              key={item.level}
              className={`milestone-item ${currentLevel >= item.level ? 'unlocked' : 'locked'}`}
            >
              <div className="milestone-level">LVL {item.level}</div>
              <div className="milestone-benefit">{item.description}</div>
              <div className="milestone-status">
                {currentLevel >= item.level ? (
                  <FaUnlock className="status-icon unlocked" />
                ) : (
                  <FaLock className="status-icon locked" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BenefitsList;
